import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { attendanceConceptService } from '@/api/domains/attendance';
import type { AttendanceConceptDetail } from '@/api/domains/attendance';


interface UseAttendanceConceptDetailPanelParams {
  conceptId: number;
}

export interface UseAttendanceConceptDetailPanelResult {
  concept: AttendanceConceptDetail | null;
  errorMessage: string | null;
}

export function useAttendanceConceptDetailPanel({
  conceptId,
}: UseAttendanceConceptDetailPanelParams): UseAttendanceConceptDetailPanelResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { setLoading } = useSidePanelLoading();

  const [concept, setConcept] = useState<AttendanceConceptDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadConcept = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setConcept(null);
      setErrorMessage(null);

      const response = await attendanceConceptService.getById(conceptId, signal);

      if (response.error?.code === 'ABORTED') return;

      if (!response.success || !response.data) {
        const message = showApiError(
          response,
          translate('attendance:attendanceConcept.messages.detailLoadError'),
        );
        setErrorMessage(message ?? translate('attendance:attendanceConcept.messages.detailLoadError'));
        setLoading(false);
        return;
      }

      setConcept(response.data);
      setLoading(false);
    },
    [conceptId, setLoading, showApiError, translate],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadConcept(controller.signal);
    return () => { controller.abort(); };
  }, [loadConcept]);

  return { concept, errorMessage };
}

export default useAttendanceConceptDetailPanel;
